// ============================================================================
// GumaHUB Protected Route Component
// ============================================================================

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PageLoading } from '@gumahub/shared';
import { useAuth } from '../hooks/useAuth.js';
import type { ModuleId } from '@gumahub/shared';

export interface ProtectedRouteProps {
  children: React.ReactNode;
  moduleId?: ModuleId;
  requiredRole?: string;
  requiredPermission?: string;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  moduleId,
  requiredRole,
  requiredPermission,
  redirectTo = '/login',
  fallback,
}) => {
  const { isAuthenticated, isLoading, hasModuleAccess, hasRole, hasPermission } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return fallback ? <>{fallback}</> : <PageLoading message="Checking authentication..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check module access if required
  if (moduleId && !hasModuleAccess(moduleId)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have access to this module.</p>
        </div>
      </div>
    );
  }

  // Check role if required
  if (moduleId && requiredRole && !hasRole(moduleId, requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Insufficient Permissions</h1>
          <p className="text-gray-600">You don't have the required role for this action.</p>
        </div>
      </div>
    );
  }

  // Check permission if required
  if (moduleId && requiredPermission && !hasPermission(moduleId, requiredPermission)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Insufficient Permissions</h1>
          <p className="text-gray-600">You don't have permission to access this resource.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
